/**
 * Magento-style global admin search index: sidebar menus, settings tabs,
 * and notable setting keywords (e.g. smtp → Email settings / SMTP server).
 */

import { SIDEBAR_SECTIONS } from '../layouts/sidebarMenuConfig';
import { MENU_TO_PATH } from './adminRoutes';

export const SETTINGS_TAB_INTENT_KEY = 'admin-settings-tab-intent';
export const EMAIL_SETTINGS_TAB_INTENT_KEY = 'admin-email-settings-tab-intent';
export const CANCELLATION_SETTINGS_TAB_INTENT_KEY = 'admin-cancellation-settings-tab-intent';

/** Extra keywords for sidebar menu ids (case-insensitive partial match). */
const MENU_KEYWORDS = {
  'email-settings': [
    'smtp',
    'smtp server',
    'smtp_host',
    'smtp_port',
    'mail',
    'mail server',
    'outgoing mail',
    'email',
    'e-mail',
  ],
  settings: ['general settings', 'system configuration', 'company', 'communication'],
  reviews: ['testimonials', 'customer reviews'],
  'review-automation-dashboard': ['review automation', 'google review', 'post-trip review'],
  'review-automation-requests': ['review requests', 'feedback request'],
  'review-automation-reports': ['review reports', 'review funnel'],
  'review-automation-settings': ['review settings', 'whatsapp review template'],
  'marketing-leads': ['user journey', 'funnels', 'attribution leads', 'leads management'],
  'marketing-campaigns': ['utm campaign', 'campaign performance', 'attribution campaigns'],
  'google-ads-dashboard': ['marketing dashboard', 'marketing & analytics', 'google ads dashboard', 'roas'],
  'utm-tracking-dashboard': ['utm', 'utm tracking', 'campaign tracking', 'attribution'],
  'utm-tracking-urls': ['tracked urls', 'utm urls', 'utm export', 'tracking url generator'],
  'seo-page-urls': [
    'page urls',
    'all pages',
    'frontend urls',
    'title',
    'primary keyword',
    'secondary keyword',
    'keyword inventory',
  ],
  'city-money-pages': [
    'city hub',
    'city money pages',
    'nashik cab booking',
    'aurangabad cab booking',
    'nagpur cab booking',
    'ahmedabad cab booking',
    'surat cab booking',
    'city landing pages',
  ],
  'seo-engine-dashboard': [
    'seo management',
    'landing page engine',
    'seo dashboard',
    'city landing pages',
  ],
  'seo-control-center': [
    'rank math',
    'seo control center',
    'seo dashboard',
    'seo score',
    'schema markup',
    'sitemap',
    'robots',
    'canonical',
    'internal linking',
    'seo analyzer',
    'setup wizard',
    'serp preview',
    'rank tracker',
    '404 monitor',
    'content overview',
    'link builder',
    'image seo',
    'page urls',
    'page url inventory',
    'primary keyword',
    'local seo',
    'index status',
    'google search console',
  ],
  'ai-seo-overview': ['ai seo', 'content automation', 'nvidia', 'gemini', 'openrouter'],
  'ai-seo-how-to': ['how to use ai seo', 'playbook', 'end to end', 'staff flow', 'approve blog', 'create keyword'],
  'ai-seo-settings': ['free models only', 'ai budget', 'auto publish', 'top keywords', 'social after approval'],
  'ai-seo-keywords': ['gsc', 'search console', 'create keyword'],
  'ai-seo-providers': ['nvidia nim', 'groq', 'ollama', 'ai provider'],
  'cancellation-settings': ['cancel', 'refund policy', 'cancellation policy'],
  'loyalty-settings': ['points', 'rewards'],
  'chatbot-settings': ['chat', 'assistant', 'widget', 'tejas fleet assistant'],
  'maintenance-settings': ['maintenance mode', 'downtime'],
  'database-settings': ['db', 'mysql', 'postgres'],
  'cron-history': ['cron history', 'cron log', 'job runs', 'scheduled runs'],
  'notification-center': ['alerts', 'realtime'],
  logs: ['email logs', 'audit', 'send log'],
  'dev-doc': [
    'dev doc',
    'developer documentation',
    'technical documentation',
    'implementation guide',
    'admin architecture',
    'frontend architecture',
    'mobile app',
    'vendor portal',
    'customer mobile',
    'api reference',
    'feature catalog',
  ],
  'sms-templates': ['sms', 'text message'],
};

/**
 * Settings page tabs under General settings (`/settings`).
 * Keep ids in sync with SettingsPage.jsx.
 */
const GENERAL_SETTINGS_TABS = [
  { id: 'company', label: 'Company', keywords: ['company', 'business name', 'address', 'gstin'] },
  { id: 'invoice', label: 'Tax invoice', keywords: ['invoice', 'tax', 'gst'] },
  { id: 'regional', label: 'Regional', keywords: ['timezone', 'currency', 'locale', 'language'] },
  { id: 'booking', label: 'Booking Rules', keywords: ['booking rules', 'advance booking'] },
  { id: 'loyalty', label: 'Loyalty points', keywords: ['loyalty', 'points', 'rewards'] },
  { id: 'chatbot', label: 'Chatbot', keywords: ['chat', 'assistant', 'widget', 'tejas fleet'] },
  { id: 'trip-type', label: 'Trip Type', keywords: ['oneway', 'roundtrip', 'trip type'] },
  { id: 'vehicles', label: 'Vehicles', keywords: ['vehicle', 'fleet settings'] },
  { id: 'payments', label: 'Payments', keywords: ['payment', 'razorpay', 'gateway'] },
  {
    id: 'communication',
    label: 'SMS & WhatsApp',
    keywords: ['sms', 'whatsapp', 'communication', 'blacksms', 'twilio'],
  },
  { id: 'integrations', label: 'Integrations', keywords: ['api key', 'integration', 'webhook'] },
  { id: 'seo', label: 'SEO & Analytics', keywords: ['seo', 'google analytics', 'gtm'] },
  { id: 'url-rewrite', label: 'URL rewrite', keywords: ['url rewrite', 'redirect', '301', '302', 'rewrite'] },
  { id: 'security', label: 'Security', keywords: ['security', 'captcha', 'recaptcha'] },
  { id: 'coupons', label: 'Coupons', keywords: ['coupon', 'promo', 'discount'] },
  { id: 'sms-templates', label: 'SMS templates', keywords: ['sms template', 'text template'] },
  { id: 'booking-status', label: 'Booking Status', keywords: ['booking status'] },
  {
    id: 'booking-search-result',
    label: 'Booking Search Result',
    keywords: ['search result', 'serp'],
  },
  { id: 'cron-jobs', label: 'Scheduled jobs', keywords: ['cron', 'schedule', 'job'] },
  { id: 'notifications', label: 'Notifications', keywords: ['notification settings'] },
  { id: 'miscellaneous', label: 'Miscellaneous', keywords: ['misc', 'miscellaneous'] },
  {
    id: 'blog',
    label: 'Blog',
    keywords: ['blog', 'blog settings', 'view count', 'views', 'share buttons', 'facebook share'],
  },
  { id: 'social-media', label: 'Social Media', keywords: ['facebook', 'instagram', 'social'] },
];

/** Email settings tabs — keep ids in sync with EmailSettingsPage.jsx. */
const EMAIL_SETTINGS_TABS = [
  {
    id: 'notifications',
    label: 'Notification emails',
    keywords: ['notification emails', 'email assignment', 'send copy', 'payment received'],
  },
  {
    id: 'chrome',
    label: 'Header & footer',
    keywords: ['email header', 'email footer', 'chrome'],
  },
  {
    id: 'smtp',
    label: 'SMTP server',
    keywords: [
      'smtp',
      'smtp server',
      'smtp_host',
      'smtp_port',
      'smtp_username',
      'smtp_password',
      'mail server',
      'outgoing mail',
      'gmail',
      'encryption',
      'from email',
      'from_name',
    ],
  },
  {
    id: 'templates',
    label: 'Template library',
    keywords: ['email template', 'template library', 'email body'],
  },
];

const CANCELLATION_SETTINGS_TABS = [
  { id: 'policy', label: 'Cancellation Policy', keywords: ['cancellation policy'] },
  { id: 'reasons', label: 'Reasons Management', keywords: ['cancel reason'] },
  { id: 'refund', label: 'Refund Settings', keywords: ['refund'] },
  { id: 'notifications', label: 'Notifications', keywords: ['cancellation notification'] },
  { id: 'automation', label: 'Automation', keywords: ['auto cancel'] },
  { id: 'other', label: 'Other Settings', keywords: [] },
];

/** Notable field / alias entries that don't map 1:1 to a menu label. */
const FIELD_ALIASES = [
  {
    id: 'field-smtp-host',
    title: 'SMTP Host',
    subtitle: 'System settings → Email settings → SMTP server',
    type: 'setting',
    menuId: 'email-settings',
    path: '/settings/email',
    tab: 'smtp',
    tabIntentKey: EMAIL_SETTINGS_TAB_INTENT_KEY,
    icon: 'fas fa-server',
    keywords: ['smtp_host', 'smtp host', 'host'],
  },
  {
    id: 'field-smtp-port',
    title: 'SMTP Port',
    subtitle: 'System settings → Email settings → SMTP server',
    type: 'setting',
    menuId: 'email-settings',
    path: '/settings/email',
    tab: 'smtp',
    tabIntentKey: EMAIL_SETTINGS_TAB_INTENT_KEY,
    icon: 'fas fa-server',
    keywords: ['smtp_port', 'smtp port', '587', '465'],
  },
];

function normalize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function haystackFor(entry) {
  const parts = [
    entry.title,
    entry.subtitle,
    entry.menuId,
    entry.path,
    entry.tab,
    ...(entry.keywords || []),
  ];
  return normalize(parts.filter(Boolean).join(' '));
}

function scoreMatch(entry, query) {
  if (!query) return 0;
  const title = normalize(entry.title);
  const haystack = entry._haystack || haystackFor(entry);
  const words = query.split(' ').filter(Boolean);
  const multiOk = words.length > 1 && words.every((w) => haystack.includes(w));
  if (!haystack.includes(query) && !multiOk) return 0;

  let score = 0;

  if (title === query) score = Math.max(score, 100);
  else if (title.startsWith(query)) score = Math.max(score, 92);
  else if (title.split(' ').some((t) => t === query)) score = Math.max(score, 88);
  else if (title.includes(query)) score = Math.max(score, 80);

  const tokens = haystack.split(' ');
  if (tokens.some((t) => t === query)) score = Math.max(score, 70);
  if (tokens.some((t) => t.startsWith(query))) score = Math.max(score, 60);
  if (haystack.includes(query)) score = Math.max(score, 50);
  if (multiOk) score = Math.max(score, title.includes(query) ? 85 : 55);

  // Prefer dedicated setting tabs when the query matches the tab id or title
  if (entry.type === 'setting' && entry.tab && (normalize(entry.tab) === query || title.includes(query))) {
    score += 8;
  }

  return score;
}

function pushUnique(map, entry) {
  if (!entry?.id || map.has(entry.id)) return;
  map.set(entry.id, {
    ...entry,
    _haystack: haystackFor(entry),
  });
}

function collectMenuEntries(items, trail, map) {
  for (const item of items || []) {
    if (!item?.id) continue;

    const label = item.label || item.id;
    const nextTrail = [...trail, label];
    const targetId = item.menuTargetId || item.id;
    const path = MENU_TO_PATH[targetId];

    if (!item.isGroup && path) {
      const section = trail[0] || '';
      pushUnique(map, {
        id: `menu:${targetId}`,
        type: 'menu',
        title: label,
        subtitle: nextTrail.slice(0, -1).join(' → ') || section || 'Navigation',
        menuId: targetId,
        path,
        icon: item.icon || 'fas fa-circle',
        keywords: [
          ...(MENU_KEYWORDS[targetId] || []),
          ...(item.keywords || []),
          item.description || '',
          item.id,
          targetId,
        ].filter(Boolean),
      });
    }

    if (item.isGroup && item.children?.length) {
      collectMenuEntries(item.children, nextTrail, map);
    }
  }
}

function buildIndex() {
  const map = new Map();

  for (const section of SIDEBAR_SECTIONS) {
    const sectionTrail = section.sectionLabel ? [section.sectionLabel] : [];
    collectMenuEntries(section.items, sectionTrail, map);
  }

  for (const tab of GENERAL_SETTINGS_TABS) {
    pushUnique(map, {
      id: `settings-tab:${tab.id}`,
      type: 'setting',
      title: tab.label,
      subtitle: 'System settings → General settings',
      menuId: 'settings',
      path: '/settings',
      tab: tab.id,
      tabIntentKey: SETTINGS_TAB_INTENT_KEY,
      icon: 'fas fa-sliders-h',
      keywords: tab.keywords || [],
    });
  }

  for (const tab of EMAIL_SETTINGS_TABS) {
    pushUnique(map, {
      id: `email-tab:${tab.id}`,
      type: 'setting',
      title: tab.label,
      subtitle: 'System settings → Email settings',
      menuId: 'email-settings',
      path: '/settings/email',
      tab: tab.id,
      tabIntentKey: EMAIL_SETTINGS_TAB_INTENT_KEY,
      icon: tab.id === 'smtp' ? 'fas fa-server' : 'fas fa-envelope',
      keywords: tab.keywords || [],
    });
  }

  for (const tab of CANCELLATION_SETTINGS_TABS) {
    pushUnique(map, {
      id: `cancellation-tab:${tab.id}`,
      type: 'setting',
      title: tab.label,
      subtitle: 'System settings → Cancellation',
      menuId: 'cancellation-settings',
      path: '/settings/cancellation',
      tab: tab.id,
      tabIntentKey: CANCELLATION_SETTINGS_TAB_INTENT_KEY,
      icon: 'fas fa-ban',
      keywords: tab.keywords || [],
    });
  }

  for (const entry of FIELD_ALIASES) {
    pushUnique(map, entry);
  }

  return Array.from(map.values());
}

let cachedIndex = null;

export function getAdminSearchIndex() {
  if (!cachedIndex) cachedIndex = buildIndex();
  return cachedIndex;
}

/**
 * Case-insensitive partial search across the admin index.
 * @param {string} rawQuery
 * @param {{ limit?: number }} [options]
 */
export function searchAdminIndex(rawQuery, options = {}) {
  const limit = options.limit ?? 12;
  const query = normalize(rawQuery);
  if (query.length < 2) return [];

  const scored = [];
  for (const entry of getAdminSearchIndex()) {
    const score = scoreMatch(entry, query);
    if (score > 0) scored.push({ entry, score });
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const aField = String(a.entry.id).startsWith('field-') ? 1 : 0;
    const bField = String(b.entry.id).startsWith('field-') ? 1 : 0;
    if (aField !== bField) return aField - bField;
    return a.entry.title.localeCompare(b.entry.title);
  });

  return scored.slice(0, limit).map(({ entry, score }) => {
    const { _haystack, ...rest } = entry;
    return { ...rest, score };
  });
}

/**
 * Build a navigable path (optional ?tab=) and persist tab intent for pages that read sessionStorage.
 */
export function prepareSearchNavigation(result) {
  if (!result) return null;
  if (result.tab && result.tabIntentKey) {
    try {
      sessionStorage.setItem(result.tabIntentKey, result.tab);
    } catch {
      /* ignore */
    }
  }
  const base = result.path || (result.menuId ? MENU_TO_PATH[result.menuId] : null);
  if (!base) return null;
  if (result.tab) {
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}tab=${encodeURIComponent(result.tab)}`;
  }
  return base;
}
